// Session watcher: bounces the user to the SSO login the moment any of
// these conditions becomes true:
//   1) the access token has expired AND silent refresh failed
//   2) the user requested a logout (handled by msal logoutRedirect)
//   3) a consent-service response was 401 (delegated by the axios interceptor)
//
// We poll once a minute as a belt-and-braces against clock drift between
// the browser and the SSO -- MSAL itself already refreshes on demand, but
// the poll guarantees we redirect *before* the next user action fails.

import { msalInstance, getActiveAccount, login } from "./msal";

const POLL_INTERVAL_MS = 60 * 1000;
const SKEW_MS = 60 * 1000; // treat anything within 1 min of expiry as expired

let timer: ReturnType<typeof setInterval> | null = null;

export function startSessionWatcher(onExpired: () => void): () => void {
  const tick = () => {
    const account = getActiveAccount();
    if (!account) return;

    const exp = account.idTokenClaims?.exp;
    if (typeof exp !== "number") return;

    const expMs = exp * 1000;
    if (Date.now() > expMs - SKEW_MS) {
      // Try silent refresh. If it fails interactively, redirect.
      msalInstance
        .acquireTokenSilent({
          scopes: ["openid", "profile"],
          account,
          forceRefresh: true,
        })
        .catch(() => {
          onExpired();
          void login(window.location.pathname);
        });
    }
  };

  tick();
  timer = setInterval(tick, POLL_INTERVAL_MS);
  return () => {
    if (timer) clearInterval(timer);
    timer = null;
  };
}
